import { hyperEVM } from '@/config/chain/constants'
import type { ChainConfigEntry } from '@/config/chain/types'
import { NATIVE_ASSET_MOCK_ADDRESS } from '@/config/consts'
import { lendingPoolAddress, wethGatewayAddress, wethGatewayConfig } from '@/config/contracts-generated'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { ActionsSettings } from '@/domain/state/actions-settings'
import { BaseUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { VaultType } from '@/features/usdxl/types/vaults'
import { assert, raise } from '@/utils/assert'
import { maxUint256 } from 'viem'
import { useChainId } from 'wagmi'
import { ApproveDelegationAction } from '../flavours/approve-delegation/types'
import { ApproveAction } from '../flavours/approve/types'
import { BorrowAction } from '../flavours/borrow/types'
import { ClaimMarketRewardsAction } from '../flavours/claim-market-rewards/types'
import { DepositAction } from '../flavours/deposit/types'
import { IsolatedBorrowAction } from '../flavours/isolated-borrow/types'
import { IsolatedDepositAction } from '../flavours/isolated-deposit/types'
import { IsolatedLendDepositAction } from '../flavours/isolated-lend-deposit/types'
import { IsolatedLendWithdrawAction } from '../flavours/isolated-lend-withdraw/types'
import { IsolatedRepayAction } from '../flavours/isolated-repay/types'
import { IsolatedWithdrawAction } from '../flavours/isolated-withdraw/types'
import { PermitAction } from '../flavours/permit/types'
import { PERMIT2_ADDRESS, Permit2SignAction } from '../flavours/permit2-sign/types'
import { RepayAction } from '../flavours/repay/types'
import { SetUseAsCollateralAction } from '../flavours/set-use-as-collateral/types'
import { SetUserEModeAction } from '../flavours/set-user-e-mode/logic/types'
import { VaultDepositAction } from '../flavours/vault-deposit/types'
import { VaultWithdrawAction } from '../flavours/vault-withdraw/types'
import { WithdrawAction } from '../flavours/withdraw/types'
import { WrapAction } from '../flavours/wrap/types'
import { Action, ActionContext, Objective } from './types'

export interface UseCreateActionsParams {
  objectives: Objective[]
  actionsSettings: ActionsSettings
  actionContext: ActionContext
}

/** React hook wrapper – delegates to the pure `createActions` function. */
export function useCreateActions({ objectives, actionsSettings }: UseCreateActionsParams): Action[] {
  const chainConfig = useChainConfigEntry()
  const chainId = useChainId()
  return createActions(objectives, actionsSettings, chainConfig, chainId)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNativeAssetInfoSymbol(chainConfig: ChainConfigEntry): TokenSymbol {
  return chainConfig.markets?.nativeAssetInfo.nativeAssetSymbol ?? raise('Native asset info is not defined')
}

/**
 * Check if a token is a native asset (HYPE).
 * Checks both by symbol (for pool markets) and by address (for eVaults/EVC).
 */
function isNativeAsset(token: Token, chainConfig: ChainConfigEntry): boolean {
  // Check by address first (more reliable)
  if (token.address.toLowerCase() === NATIVE_ASSET_MOCK_ADDRESS.toLowerCase()) {
    return true
  }
  // Also check by symbol for backwards compatibility
  try {
    return token.symbol === getNativeAssetInfoSymbol(chainConfig)
  } catch {
    // If chain config doesn't have markets, just rely on address check
    return false
  }
}

// ---------------------------------------------------------------------------
// Pure function – testable without React
// ---------------------------------------------------------------------------

/**
 * Map an array of objectives to the corresponding action sequence.
 *
 * This is the pure-logic core of `useCreateActions`.  It is exported so
 * that unit tests can call it directly without React / hook infrastructure.
 */
export function createActions(
  objectives: Objective[],
  actionsSettings: ActionsSettings,
  chainConfig: ChainConfigEntry,
  chainId: number,
): Action[] {
  return objectives.flatMap((objective): Action[] => {
    // @note: you can create hooks (actions) conditionally, but ensure that component will be re-mounted when condition changes
    // to accomplish this, tweak stringifyObjectivesToStableActions function

    switch (objective.type) {
      case 'deposit': {
        const lendingPool = getContractAddress(lendingPoolAddress, chainId)

        const depositAction: DepositAction = {
          type: 'deposit',
          token: objective.token,
          value: objective.value,
        }

        if (isNativeAsset(objective.token, chainConfig)) {
          return [depositAction]
        }

        if (actionsSettings.preferPermits && chainConfig.permitSupport[objective.token.address]) {
          const permitAction: PermitAction = {
            type: 'permit',
            token: objective.token,
            spender: lendingPool,
            value: objective.value,
          }

          return [permitAction, depositAction]
        }

        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.token,
          spender: lendingPool,
          value: objective.value,
        }
        return [approveAction, depositAction]
      }

      case 'withdraw': {
        const wethGateway = getContractAddress(wethGatewayAddress, chainId)

        const withdrawValue = objective.all
          ? objective.reserve.token.fromBaseUnit(BaseUnitNumber(maxUint256))
          : objective.value

        if (isNativeAsset(objective.reserve.token, chainConfig)) {
          assert(objective.gatewayApprovalValue, 'gatewayApprovalValue is required for native asset')
          const approveAction: ApproveAction = {
            type: 'approve',
            token: objective.reserve.aToken,
            spender: wethGateway,
            value: objective.gatewayApprovalValue,
          }

          const withdrawAction: WithdrawAction = {
            type: 'withdraw',
            token: objective.reserve.token,
            value: withdrawValue,
          }
          return [approveAction, withdrawAction]
        }

        const withdrawAction: WithdrawAction = {
          type: 'withdraw',
          token: objective.reserve.token,
          value: withdrawValue,
        }
        return [withdrawAction]
      }

      case 'isolatedWithdraw': {
        const withdrawAction: IsolatedWithdrawAction = {
          type: 'isolatedWithdraw',
          token: objective.token,
          value: objective.value,
          pairAddress: objective.pairAddress,
        }
        return [withdrawAction]
      }

      case 'isolatedDeposit': {
        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.token,
          spender: objective.pairAddress,
          value: objective.value,
        }

        const depositAction: IsolatedDepositAction = {
          type: 'isolatedDeposit',
          token: objective.token,
          value: objective.value,
        }
        return [approveAction, depositAction]
      }

      case 'isolatedLendWithdraw': {
        const withdrawAction: IsolatedLendWithdrawAction = {
          type: 'isolatedLendWithdraw',
          token: objective.token,
          value: objective.value,
          requestedValue: objective.requestedValue,
          poolAvailable: objective.poolAvailable,
          pairAddress: objective.pairAddress,
        }
        return [withdrawAction]
      }

      case 'isolatedLendDeposit': {
        // Check if we need to wrap native token (HYPE → WHYPE)
        const needsWrap = isNativeAsset(objective.token, chainConfig)
        // Use wrapped token for approve and deposit if wrapping native
        const depositToken = needsWrap ? objective.wrappedToken : objective.token

        const depositAction: IsolatedLendDepositAction = {
          type: 'isolatedLendDeposit',
          token: depositToken,
          value: objective.value,
          pairAddress: objective.pairAddress,
        }

        // Native tokens need to be wrapped first, then approved
        if (needsWrap) {
          const wrapAction: WrapAction = {
            type: 'wrap',
            token: objective.wrappedToken,
            value: objective.value,
          }
          const approveAction: ApproveAction = {
            type: 'approve',
            token: depositToken,
            spender: objective.pairAddress,
            value: objective.value,
          }
          return [wrapAction, approveAction, depositAction]
        }

        // ERC20 tokens just need approval
        const approveAction: ApproveAction = {
          type: 'approve',
          token: depositToken,
          spender: objective.pairAddress,
          value: objective.value,
        }
        return [approveAction, depositAction]
      }

      case 'isolatedBorrow': {
        const borrowAction: IsolatedBorrowAction = {
          type: 'isolatedBorrow',
          token: objective.token,
          value: objective.value,
        }
        return [borrowAction]
      }

      case 'isolatedRepay': {
        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.token,
          spender: hyperEVM.contracts.repayHelper.address,
          value: objective.approvalValue || objective.value,
        }

        const repayAction: IsolatedRepayAction = {
          type: 'isolatedRepay',
          token: objective.token,
          value: objective.value,
          pairAddress: objective.pairAddress,
          shares: objective.shares,
          repayFull: objective.repayFull,
        }
        return [approveAction, repayAction]
      }

      case 'vaultDeposit': {
        const depositAction: VaultDepositAction = {
          type: 'vaultDeposit',
          token: objective.token,
          vaultType: objective.vaultType,
          value: objective.value,
          vaultAddress: objective.vaultAddress,
          tellerVaultAddress: objective.tellerVaultAddress,
          tokenOutAddress: objective.tokenOutAddress,
          swapPath: objective.swapPath,
          rateInQuote: objective.rateInQuote,
          swapVaultContractAddress: objective.swapVaultContractAddress,
          isTellerBouncerVault: objective.isTellerBouncerVault,
          evcAddress: objective.evcAddress,
          receiver: objective.receiver,
          enableAsCollateral: objective.enableAsCollateral,
        }

        // Native assets (HYPE) don't need approval - they're sent as value in the EVC batch
        if (isNativeAsset(objective.token, chainConfig)) {
          return [depositAction]
        }

        // For EEARN vaults (Euler eLend), use Permit2 flow ONLY if evcAddress is available
        // Without evcAddress, the deposit action will fall back to standard ERC20 deposit
        // which requires vault approval, not Permit2 approval
        if (objective.vaultType === VaultType.EEARN && objective.evcAddress) {
          // 1. Approve Permit2 (one-time, ERC20 approval)
          const approvePermit2Action: ApproveAction = {
            type: 'approve',
            token: objective.token,
            spender: PERMIT2_ADDRESS,
            value: objective.value,
          }

          // 2. Sign Permit2 message (allows vault to use Permit2 to pull tokens)
          const permit2SignAction: Permit2SignAction = {
            type: 'permit2Sign',
            token: objective.token,
            amount: objective.value,
            spender: objective.vaultAddress,
          }

          return [approvePermit2Action, permit2SignAction, depositAction]
        }

        // For other vault types OR EEARN without evcAddress, use regular ERC20 approval
        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.token,
          spender: objective.swapVaultContractAddress
            ? objective.swapVaultContractAddress
            : objective.isTellerBouncerVault
              ? objective.tellerVaultAddress
              : objective.vaultAddress,
          value: objective.value,
        }

        return [approveAction, depositAction]
      }

      case 'vaultWithdraw': {
        const withdrawAction: VaultWithdrawAction = {
          type: 'vaultWithdraw',
          token: objective.token,
          value: objective.value,
          vaultType: objective.vaultType,
          vaultAddress: objective.vaultAddress,
          atomicQueueVaultAddress: objective.atomicQueueVaultAddress,
          rateInQuoteSafeWithFee: objective.rateInQuoteSafeWithFee,
          depositToken: objective.depositToken,
          deadline: objective.deadline,
          unwrapToNative: objective.unwrapToNative,
          userShares: objective.userShares,
          isMax: objective.isMax,
        }

        // EEARN vaults with unwrapToNative don't need approval - they redeem to EVC then unwrap
        // HYPERBEAT vaults also don't need approval
        if (objective.vaultType === VaultType.EEARN && objective.unwrapToNative) {
          return [withdrawAction]
        }

        if (objective.vaultType === VaultType.HYPERBEAT) {
          return [withdrawAction]
        }

        // EEARN standard redeem doesn't need approval (shares are burned, not transferred)
        if (objective.vaultType === VaultType.EEARN) {
          return [withdrawAction]
        }

        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.token,
          spender: objective.atomicQueueVaultAddress,
          value: objective.value,
        }

        return [approveAction, withdrawAction]
      }

      case 'borrow': {
        if (isNativeAsset(objective.token, chainConfig)) {
          const wethGatewayAddress = getContractAddress(wethGatewayConfig.address, chainId)

          const approveDelegationAction: ApproveDelegationAction = {
            type: 'approveDelegation',
            token: objective.token,
            value: objective.value,
            spender: wethGatewayAddress,
          }

          const borrowAction: BorrowAction = {
            type: 'borrow',
            token: objective.token,
            value: objective.value,
          }
          return [approveDelegationAction, borrowAction]
        }

        const borrowAction: BorrowAction = {
          type: 'borrow',
          token: objective.token,
          value: objective.value,
        }
        return [borrowAction]
      }

      case 'repay': {
        const lendingPool = getContractAddress(lendingPoolAddress, chainId)

        const repayAction: RepayAction = {
          type: 'repay',
          reserve: objective.reserve,
          value: objective.value,
          useAToken: objective.useAToken,
          repayFull: objective.repayFull,
        }

        if (isNativeAsset(objective.reserve.token, chainConfig) || objective.useAToken) {
          return [repayAction]
        }

        if (actionsSettings.preferPermits && chainConfig.permitSupport[objective.reserve.token.address]) {
          const permitAction: PermitAction = {
            type: 'permit',
            token: objective.reserve.token,
            spender: lendingPool,
            value: objective.value,
          }

          return [permitAction, repayAction]
        }

        const approveAction: ApproveAction = {
          type: 'approve',
          token: objective.reserve.token,
          spender: lendingPool,
          requiredValue: objective.requiredApproval,
          value: objective.value,
        }

        return [approveAction, repayAction]
      }

      case 'setUseAsCollateral': {
        const setUseAsCollateralAction: SetUseAsCollateralAction = {
          type: 'setUseAsCollateral',
          token: objective.token,
          useAsCollateral: objective.useAsCollateral,
        }
        return [setUseAsCollateralAction]
      }

      case 'setUserEMode': {
        const setUserEModeAction: SetUserEModeAction = {
          type: 'setUserEMode',
          eModeCategoryId: objective.eModeCategoryId,
        }
        return [setUserEModeAction]
      }

      case 'claimMarketRewards': {
        const ClaimMarketRewardsActions: ClaimMarketRewardsAction = {
          type: 'claimMarketRewards',
          token: objective.token,
          incentiveControllerAddress: objective.incentiveControllerAddress,
          assets: objective.assets,
        }

        return [ClaimMarketRewardsActions]
      }

      case 'permit2Sign': {
        // Permit2Sign is a special action that signs a typed message
        // It's not converted to anything else, it IS the action
        const permit2SignAction: Permit2SignAction = {
          type: 'permit2Sign',
          token: objective.token,
          amount: objective.amount,
          spender: objective.spender,
        }
        return [permit2SignAction]
      }

      default: {
        console.warn(`Unhandled objective type: ${(objective as Objective).type}`)
        return []
      }
    }
  })
}
